import React, { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Spinner } from "@/components/ui";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Members from "@/pages/Members";
import MemberDetail from "@/pages/MemberDetail";
import RiskStratification from "@/pages/RiskStratification";
import WorkQueue from "@/pages/WorkQueue";
import Assessments from "@/pages/Assessments";
import CarePlans from "@/pages/CarePlans";
import Providers from "@/pages/Providers";
import Billing from "@/pages/Billing";
import UtilizationManagement from "@/pages/UtilizationManagement";
import QualityManagement from "@/pages/QualityManagement";
import Analytics from "@/pages/Analytics";
import MemberPortal from "@/pages/MemberPortal";
import ProviderPortal from "@/pages/ProviderPortal";
import Monetization from "@/pages/Monetization";
import McoPlaybook from "@/pages/McoPlaybook";
import Interoperability from "@/pages/Interoperability";
import Tenants from "@/pages/Tenants";
import CareGaps from "@/pages/CareGaps";
import ContactCenter from "@/pages/ContactCenter";
import SLA from "@/pages/SLA";
import EdiTransport from "@/pages/EdiTransport";
import ProductTour from "@/pages/ProductTour";
import SalesPlaybook from "@/pages/SalesPlaybook";
import RfpResponse from "@/pages/RfpResponse";
import DeliveryModel from "@/pages/DeliveryModel";
import AiOperatingModel from "@/pages/AiOperatingModel";
import TrilliumApproach from "@/pages/TrilliumApproach";
import GoToMarket from "@/pages/GoToMarket";
import FounderCredibility from "@/pages/FounderCredibility";
import Showcase from "@/pages/Showcase";
import Demo from "@/pages/Demo";
import TrilliumDemoVideos from "@/pages/TrilliumDemoVideos";
import SubmissionCenter from "@/pages/SubmissionCenter";
import Help from "@/pages/Help";
import SecurityCompliance from "@/pages/SecurityCompliance";
import WinningPlaybook from "@/pages/WinningPlaybook";
import StaffingPlan from "@/pages/StaffingPlan";
import JobDescriptions from "@/pages/JobDescriptions";

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sid } });
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate(data.role === "member" ? "/member-portal" : data.role === "provider" ? "/provider-portal" : "/dashboard", { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return <div className="min-h-screen flex items-center justify-center bg-surface"><Spinner label="Completing sign-in…" /></div>;
}

const homeFor = (role) => (role === "member" ? "/member-portal" : role === "provider" ? "/provider-portal" : "/dashboard");

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-surface"><Spinner label="Loading Go MCO…" /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user && user !== false) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/showcase" element={<Showcase />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/demo-videos" element={<TrilliumDemoVideos />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/dashboard" element={<Protected roles={["admin", "care_manager", "provider"]}><Dashboard /></Protected>} />
      <Route path="/tour" element={<Protected roles={["admin", "care_manager", "provider"]}><ProductTour /></Protected>} />
      <Route path="/members" element={<Protected roles={["admin", "care_manager", "provider"]}><Members /></Protected>} />
      <Route path="/members/:id" element={<Protected roles={["admin", "care_manager", "provider"]}><MemberDetail /></Protected>} />
      <Route path="/risk" element={<Protected roles={["admin", "care_manager"]}><RiskStratification /></Protected>} />
      <Route path="/work-queue" element={<Protected roles={["admin", "care_manager"]}><WorkQueue /></Protected>} />
      <Route path="/assessments" element={<Protected roles={["admin", "care_manager"]}><Assessments /></Protected>} />
      <Route path="/care-plans" element={<Protected roles={["admin", "care_manager"]}><CarePlans /></Protected>} />
      <Route path="/care-gaps" element={<Protected roles={["admin", "care_manager"]}><CareGaps /></Protected>} />
      <Route path="/providers" element={<Protected roles={["admin", "care_manager"]}><Providers /></Protected>} />
      <Route path="/billing" element={<Protected roles={["admin", "care_manager"]}><Billing /></Protected>} />
      <Route path="/utilization" element={<Protected roles={["admin", "care_manager"]}><UtilizationManagement /></Protected>} />
      <Route path="/quality" element={<Protected roles={["admin", "care_manager"]}><QualityManagement /></Protected>} />
      <Route path="/contact-center" element={<Protected roles={["admin", "care_manager"]}><ContactCenter /></Protected>} />
      <Route path="/sla" element={<Protected roles={["admin", "care_manager"]}><SLA /></Protected>} />
      <Route path="/analytics" element={<Protected roles={["admin", "care_manager"]}><Analytics /></Protected>} />
      <Route path="/interoperability" element={<Protected roles={["admin", "care_manager"]}><Interoperability /></Protected>} />
      <Route path="/edi-transport" element={<Protected roles={["admin", "care_manager"]}><EdiTransport /></Protected>} />
      <Route path="/member-portal" element={<Protected roles={["admin", "member"]}><MemberPortal /></Protected>} />
      <Route path="/provider-portal" element={<Protected roles={["admin", "provider"]}><ProviderPortal /></Protected>} />
      <Route path="/tenants" element={<Protected roles={["admin"]}><Tenants /></Protected>} />
      <Route path="/sales-playbook" element={<Protected roles={["admin"]}><SalesPlaybook /></Protected>} />
      <Route path="/rfp-response" element={<Protected roles={["admin"]}><RfpResponse /></Protected>} />
      <Route path="/delivery-model" element={<Protected roles={["admin"]}><DeliveryModel /></Protected>} />
      <Route path="/ai-operated-model" element={<Protected roles={["admin"]}><AiOperatingModel /></Protected>} />
      <Route path="/trillium-approach" element={<Protected roles={["admin"]}><TrilliumApproach /></Protected>} />
      <Route path="/go-to-market" element={<Protected roles={["admin"]}><GoToMarket /></Protected>} />
      <Route path="/submission" element={<Protected roles={["admin"]}><SubmissionCenter /></Protected>} />
      <Route path="/winning-playbook" element={<Protected roles={["admin"]}><WinningPlaybook /></Protected>} />
      <Route path="/staffing" element={<Protected roles={["admin"]}><StaffingPlan /></Protected>} />
      <Route path="/job-descriptions" element={<Protected roles={["admin"]}><JobDescriptions /></Protected>} />
      <Route path="/help" element={<Protected roles={["admin"]}><Help /></Protected>} />
      <Route path="/compliance" element={<Protected roles={["admin", "care_manager", "provider"]}><SecurityCompliance /></Protected>} />
      <Route path="/founder-credibility" element={<Protected roles={["admin"]}><FounderCredibility /></Protected>} />
      <Route path="/monetization" element={<Protected roles={["admin"]}><Monetization /></Protected>} />
      <Route path="/mco-playbook" element={<Protected roles={["admin"]}><McoPlaybook /></Protected>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <Toaster position="top-right" richColors />
            <AppRouter />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}
